import { useEffect, useCallback, useRef, useState } from 'react';
import { syncEngine, type SyncEngineStatus } from '@/lib/sync';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

export interface SyncState {
  status: SyncEngineStatus;
  error: string | null;
  lastSyncedAt: string | null;
  isSyncing: boolean;
  isOffline: boolean;
  triggerSync: () => void;
}

export function useSync(): SyncState {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const setLastSyncedAt = useWorkspaceStore((s) => s.setLastSyncedAt);
  const lastSyncedAt = useWorkspaceStore((s) => s.lastSyncedAt);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const addToast = useUIStore((s) => s.addToast);

  const [status, setStatus] = useState<SyncEngineStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const prevStatusRef = useRef<SyncEngineStatus>('idle');

  // Subscribe to sync engine status
  useEffect(() => {
    const unsubscribe = syncEngine.subscribe((newStatus, syncError) => {
      setStatus(newStatus);
      setError(syncError ?? null);

      if (newStatus === 'idle' && prevStatusRef.current === 'syncing') {
        setLastSyncedAt(new Date().toISOString());
      }

      if (newStatus === 'error' && syncError) {
        addToast({
          title: 'Sync failed',
          description: syncError,
          variant: 'error',
          duration: 6000,
        });
      }

      prevStatusRef.current = newStatus;
    });

    return unsubscribe;
  }, [setLastSyncedAt, addToast]);

  // Start/stop auto-sync based on auth and workspace
  useEffect(() => {
    if (!isAuthenticated || !activeWorkspaceId) {
      syncEngine.stopAutoSync();
      return;
    }

    syncEngine.startAutoSync(activeWorkspaceId);

    // Initial pull on mount
    syncEngine.sync(activeWorkspaceId).catch(() => {
      // Error is handled by the listener above
    });

    return () => syncEngine.stopAutoSync();
  }, [isAuthenticated, activeWorkspaceId]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      if (activeWorkspaceId) {
        syncEngine.sync(activeWorkspaceId).catch(() => {
          // handled by listener
        });
      }
    };

    const handleOffline = () => {
      setStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [activeWorkspaceId]);

  const triggerSync = useCallback(() => {
    if (!activeWorkspaceId) {
      return;
    }
    syncEngine.sync(activeWorkspaceId).catch(() => {
      // handled by listener
    });
  }, [activeWorkspaceId]);

  return {
    status,
    error,
    lastSyncedAt,
    isSyncing: status === 'syncing',
    isOffline: status === 'offline' || !navigator.onLine,
    triggerSync,
  };
}
